/**
 * Account-level write-through for the playback preference.
 *
 * Kept separate from `playbackPreference.ts` (which is pure localStorage +
 * events and importable in any/early context) so the Supabase client
 * dependency only loads where the write-through actually runs.
 *
 * Write-only. The hydration READ lives in AuthContext.fetchProfile, which
 * already fetches the profile row — so this module never queries.
 */
import { supabase } from "@/integrations/supabase/client";
import type { PlaybackPreference } from "./playbackPreference";

/**
 * Persist the user's playback preference to their profile row (the
 * cross-device source of truth). Fire-and-forget + best-effort: a failure
 * leaves the device-local value intact and is implicitly retried on the next
 * change. Never throws — fully self-contained try/catch.
 */
export async function persistPlaybackPreferenceToAccount(
  userId: string,
  value: PlaybackPreference,
): Promise<void> {
  try {
    const { error } = await supabase
      .from("profiles")
      .update({ playback_preference: value })
      .eq("id", userId);
    if (error) {
      console.warn("[playback-pref] account persist failed:", error.message);
    }
  } catch (err) {
    console.warn(
      "[playback-pref] account persist threw:",
      err instanceof Error ? err.message : err,
    );
  }
}
